import { useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { callApi, handleErrorCommon } from "../common/api/Api";
import { MainContainer, MainContent } from "../common/components/Layout";
import "./Serial.css";
import { getListForUser, Serial, downloadActivateFile } from "./SerialApi";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { isJp } from "../i18n/LangUtil";
import { RegisterSerialButton } from "./RegisterSerialButton";
import { RegisterLicenseButton } from "./RegisterLicenseButton";
import { licenseNumberForView } from "../common/utils/Utils";
import { Link, Button } from "@mui/material";

const VIEW_DATE_FORMAT = "yyyy/MM/dd";

/**
 * ユーザー画面のコンポーネントです。
 *
 * @returns ユーザー画面のコンポーネント
 */
export const SerialPage = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { showBoundary } = useErrorBoundary();
  const [serialList, setSerialList] = useState<Serial[]>();

  const dateForView = (targetDate: Date) => {
    const utcDate = new Date(targetDate);
    if (isJp(i18n)) {
      return format(utcToZonedTime(utcDate, "Asia/Tokyo"), VIEW_DATE_FORMAT);
    }
    return format(utcToZonedTime(utcDate, "UTC"), VIEW_DATE_FORMAT);
  };

  const downloadClicked = (serial: Serial) => {
    (async () => {
      await callApi(async () => {
        const result = await downloadActivateFile(serial);
        if (result.error) {
          return handleErrorCommon(result.error?.response?.data, result.error, showBoundary, t);
        }
        if (!result.data) {
          return;
        }
        const blob = new Blob([result.data.data], { type: result.data.data.type });
        const link = document.createElement("a");
        link.download = result.data.fileName;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      });
    })();
  };

  useEffect(() => {
    (async () => {
      await callApi(async () => {
        const result = await getListForUser();
        if (result.error) {
          return handleErrorCommon(result.error?.response?.data, result.error, showBoundary, t);
        }

        if (!result.data || !result.data.serials || result.data.serials.length === 0) {
          setSerialList([]);
          return;
        }

        setSerialList(result.data.serials);
      });
    })();
  }, []);

  if (!serialList) {
    return <></>;
  }

  return (
    <MainContainer>
      <MainContent>
        <div className="serialContainer">
          <div
            style={{
              width: "60rem",
              height: "4rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "left",
            }}
          >
            <RegisterSerialButton
              serialList={serialList}
              onRegister={(serial) => {
                setSerialList([...serialList, serial]);
              }}
            />
          </div>
          <div style={{ height: "2rem" }} />
          <div className="serialRow">
            <div className="th">{t("serial.cameraHeader")}</div>
            <div className="th">
              <div className="w-40 flex justify-center">{t("serial.optionHeader")}</div>
              <div className="w-40 flex justify-center">{t("serial.registerHeader")}</div>
            </div>
            <div className="th">{t("serial.licenseFileHeader")}</div>
          </div>
          {serialList.map((serial) => (
            <div key={serial.id} className="serialRow">
              <div className="td">{serial.serialNumber}</div>
              <div className="td">
                {serial.licenses.map((license) => (
                  <div className="td2 flex">
                    <div className="w-40">
                      {`${isJp(i18n) ? license.option.nameJa : license.option.nameEn}`}
                      <br />
                      {`${licenseNumberForView(license.licenseNumber)}`}
                    </div>
                    <div className="w-40">{`${dateForView(license.licenseAt)}`}</div>
                  </div>
                ))}
                <div className="td2">
                  <RegisterLicenseButton
                    onRegister={(serialList) => {
                      setSerialList(serialList);
                    }}
                    targetSerial={serial}
                    serialList={serialList}
                  />
                </div>
              </div>
              <div className="td">
                {serial.licenses && serial.licenses.length > 0 && (
                  <Button variant="contained" onClick={() => downloadClicked(serial)}>
                    {t("serial.downloadButton")}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div style={{ height: "2rem" }} />
        <div className="flex flex-col items-center">
          <ul className="list-disc">
            <li>{t("serial.contactInfoMessage")}</li>
            <Link href={t("serial.contactUrl")} target="_blank" rel="noopener">
              {t("serial.contactUrlTitle")}
            </Link>
            <li>{t("serial.updateInfoMessage")}</li>
            <Link href={t("serial.updateInfoUrl")} target="_blank" rel="noopener">
              {t("serial.updateInfoUrlTitle")}
            </Link>
          </ul>
        </div>
      </MainContent>
    </MainContainer>
  );
};
